import type { CatalogItem } from "@dishour/shared";
import { estimatedRevenue } from "../api/supplier.js";
import { isQtyWellFormed } from "./quantity.js";

/** Raw per-sku quantity input, keyed by sku id. May contain empty or malformed strings mid-typing. */
export type Cart = Record<string, string>;

export type CartLine = { sku: CatalogItem; qty: string };

/**
 * Only entries with a positive, step-valid quantity become a real line — an emptied or
 * malformed input silently drops that line rather than blocking the whole cart, since the
 * user is still actively typing in the other rows.
 */
export function cartLines(cart: Cart, catalog: CatalogItem[]): CartLine[] {
  return catalog
    .filter((sku) => cart[sku.id] && isQtyWellFormed(cart[sku.id]!, sku))
    .map((sku) => ({ sku, qty: cart[sku.id]! }));
}

/** Display-only estimated total. The amount actually invoiced always comes from the server. */
export function cartTotal(lines: CartLine[]): string {
  const cents = lines.reduce(
    (sum, line) => sum + Math.round(Number(estimatedRevenue(line.qty, line.sku.unitPrice)) * 100),
    0,
  );
  return (cents / 100).toFixed(2);
}

export function toOrderLines(lines: CartLine[]): { skuId: string; qty: string }[] {
  return lines.map((line) => ({ skuId: line.sku.id, qty: line.qty }));
}
